import * as Sentry from '@sentry/browser'
import React from 'react'

import { Alert } from '../alert'

type Props = {
  /** Child components for which errors are controlled. */
  children: React.ReactNode
  /** True if the boundary should continue to show children once the error occurs. */
  showsChildrenOnError: boolean
}
interface ErrorInfo {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}
type State = {
  error: Error | null
}

/** Class used for handling errors inside the app. */
class ErrorBoundary extends React.Component<Props, State> {
  static getDerivedStateFromError(error: Error): { error: Error } {
    // Update state so the next render will show the fallback UI.
    return { error }
  }

  static defaultProps = {
    showsChildrenOnError: true,
  }

  state = {
    error: null,
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope((scope) => {
      Object.keys(errorInfo).forEach((key: string) => {
        scope.setExtra(key, errorInfo[key])
      })
      Sentry.captureException(error)
    })
  }

  render(): JSX.Element {
    const { error } = this.state
    const { children, showsChildrenOnError } = this.props

    return (
      <>
        {error && (
          <Alert
            variant="danger"
            onClose={() => this.setState({ error: null })}
            autoDismiss={false}
          >
            {(error! as Error).message}
          </Alert>
        )}
        {(!error || showsChildrenOnError) && children}
      </>
    )
  }
}

export default ErrorBoundary
